/** @jsx jsx */
import React, { useMemo, useEffect, useCallback } from 'react';
import type { Dispatch } from 'redux';
import { jsx } from '@compiled/react';
import { useCrossFlow } from '@atlassiansox/cross-flow-component-support';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import type { IssueType } from '@atlassian/jira-issue-shared-types/src/common/types/issue-hierarchy-type.tsx';
import { SectionHeadingTitle } from '@atlassian/jira-issue-view-common/src/component/section-heading/section-heading-view.tsx';
import { connect } from '@atlassian/jira-issue-view-react-redux/src/index.tsx';
import { createConfluencePageLinkRequest } from '@atlassian/jira-issue-view-store/src/actions/confluence-pages-actions.tsx';
import {
	ContextualAnalyticsData,
	SCREEN,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import {
	FREE_EDITION,
	STANDARD_EDITION,
	PREMIUM_EDITION,
} from '@atlassian/jira-shared-types/src/edition.tsx';
import { toHref } from '@atlassian/jira-shared-types/src/general.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { useAdControlsFunctionality } from '../common/ad-controls/utils.tsx';
import AdControls from '../common/ad-controls/view.tsx';
import { USER_PROPERTY_DUMMY_LINK } from '../common/constants.tsx';
import { useIsCrossFlowEligible } from '../common/cross-flow-eligibility.tsx';
import {
	getConfluencePlaceholderTemplate,
	useFireCrossFlowAnalyticsEvents,
	getSpaceKeyAndUpdateUrl,
	openCffeForConfluence,
} from '../common/utils.tsx';
import { useConfluenceDummyLinkContext } from '../controllers/confluence-dummy-link-context/index.tsx';
import { useDraftEpModal } from '../controllers/use-draft-embedded-page-modal-entry-point/use-draft-ep-modal.tsx';
import { useUserHasAddProductPermissions } from '../controllers/use-user-has-add-product-permission/index.tsx';
import { useHasRequestedTrialLozengeText } from '../controllers/use-user-has-requested-trial-lozenge-text/index.tsx';
import { createConfluenceDraft } from '../services/confluence/create-confluence-draft/index.tsx';
import { getActivePageUrl } from '../services/confluence/utils.tsx';
import { ConfluenceLink } from './dummy-confluence-link-container.tsx';
import { BackgroundProvisioningEditor } from './experiments/background-provisioning-editor/index.tsx';
import messages from './messages.tsx';
import { useIssueViewEndUsersCrossFlowViaE2EExperiment } from './placeholder-template-end-user-e2e/hooks/use-issue-view-end-users-cross-flow-via-e2e-experiment.tsx';
import { DummySectionHeading } from './styled.tsx';
import { InfoIconWithPopup } from './ui/dummy-link-popup/index.tsx';

type Props = {
	issueType?: IssueType;
	tenantHasConfluenceAppLinks?: boolean;
	projectName?: string;
	projectId?: string;
	onLinkPage?: (link: string, analyticsEvent: UIAnalyticsEvent) => void;
};
export const DummyConfluenceLinkViewComponent = ({
	issueType,
	tenantHasConfluenceAppLinks,
	projectName,
	projectId,
	onLinkPage,
}: Props) => {
	const { formatMessage } = useIntl();
	const template = useMemo(() => getConfluencePlaceholderTemplate(issueType), [issueType]);
	const crossFlow = useCrossFlow();
	const issueKey = useIssueKey();
	const appEditions = useAppEditions();

	const { isDismissed, onDismissAd } = useAdControlsFunctionality({
		userPropertyPlaceholderTemplate: USER_PROPERTY_DUMMY_LINK,
	});

	const { isCrossFlowEligible } = useIsCrossFlowEligible();

	const isCrossFlowEnabled = isCrossFlowEligible && !tenantHasConfluenceAppLinks;

	const { areProductSuggestionsEnabled, cloudId } = useTenantContext();

	// Only show dummy link to Free, Standard and Premium sites
	const showDummyLink =
		areProductSuggestionsEnabled &&
		!!template &&
		isCrossFlowEnabled &&
		!isDismissed &&
		(appEditions.software === FREE_EDITION ||
			appEditions.software === STANDARD_EDITION ||
			appEditions.software === PREMIUM_EDITION);

	const { canProvisionConfluence, fetchStatus } = useUserHasAddProductPermissions({
		embeddedConfluenceSource: 'jswIssueView',
		skip: !showDummyLink, // Skip permission check if these experiences are disabled
	});
	const { fireViewedEvent, fireClickedEvent } =
		useFireCrossFlowAnalyticsEvents(canProvisionConfluence);
	const epModalEntryPointContainer = useDraftEpModal({
		...(fg('jira_issue_view_expand_modeless_ep') && { linkConfluencePage: onLinkPage }),
	});
	const { isEpModalOpen, setIsEpModalOpen, setActivePageUrl } = useConfluenceDummyLinkContext();
	const tenantIsOnFreeEdition = appEditions.software === FREE_EDITION;

	useEffect(() => {
		if (showDummyLink && fetchStatus !== 'pending') {
			fireViewedEvent();
		}
	}, [showDummyLink, fireViewedEvent, fetchStatus]);

	// upon successful crossflow, the window is reloaded with href should have spaceKey as a query param
	const initializeDraftAndOpenModal = useCallback(async () => {
		const spaceKey = getSpaceKeyAndUpdateUrl();
		if (!spaceKey || !template) {
			return;
		}
		const result = await createConfluenceDraft(
			cloudId,
			spaceKey,
			template.blueprintModuleCompleteKey,
		);
		if (result) {
			const activePageUrl = getActivePageUrl(result.contentId, result.draftShareId, spaceKey);
			setActivePageUrl(activePageUrl);
			setIsEpModalOpen(true);
		}
	}, [cloudId, setActivePageUrl, setIsEpModalOpen, template]);

	useEffect(() => {
		initializeDraftAndOpenModal();
	}, [initializeDraftAndOpenModal]);

	const [requestStatusLozengeText, recomputeHasRequestedTrialState] =
		useHasRequestedTrialLozengeText();

	const issueViewEndUsersCrossFlowViaE2EExp = useIssueViewEndUsersCrossFlowViaE2EExperiment();

	const jswIssueLink =
		typeof window !== 'undefined' ? `${window.location.origin}/browse/${issueKey}` : undefined;

	// exposure for new experiment is on the onClick
	const onClickConfluencePlaceholder = useCallback(
		async (e: React.MouseEvent) => {
			// prevents page from reloading
			e.preventDefault();
			if (canProvisionConfluence) {
				fireClickedEvent('confluencePlaceholderTemplateLink', 'placeholderTemplateE2EIteration');

				const experimentalOptions = {
					jswIssueType: template && template.suggestedIssueType,
					projectName: projectName || '',
					jswIssueLink,
				};

				await openCffeForConfluence(
					crossFlow,
					'confluencePlaceholderTemplateScreen',
					'confluencePlaceholderTemplateLink',
					experimentalOptions,
				);
			} else if (!canProvisionConfluence && !tenantIsOnFreeEdition) {
				fireClickedEvent(
					'confluencePlaceholderTemplateLink',
					'placeholderTemplateEndUserRequestExperiment',
				);

				await openCffeForConfluence(
					crossFlow,
					'confluencePlaceholderTemplateScreen',
					'confluencePlaceholderTemplateLink',
					{
						placeholderTemplateEndUserRequestExperiment: true,
					},
				);

				recomputeHasRequestedTrialState();
			} else if (
				issueViewEndUsersCrossFlowViaE2EExp.experimentIsEnabled &&
				issueViewEndUsersCrossFlowViaE2EExp.isEnrolledInExperiment()
			) {
				issueViewEndUsersCrossFlowViaE2EExp.fireExposureEvent();

				if (issueViewEndUsersCrossFlowViaE2EExp.isVariation()) {
					fireClickedEvent(
						'confluencePlaceholderTemplateLink',
						'issueViewEndUsersCrossFlowViaE2EExperiment',
					);

					const experimentalOptions = {
						jswIssueType: template && template.suggestedIssueType,
						projectName: projectName || '',
						jswIssueLink,
					};
					await openCffeForConfluence(
						crossFlow,
						'confluencePlaceholderTemplateScreen',
						'placeholderTemplateEndUsersCrossFlowViaE2EFlow',
						experimentalOptions,
					);
				} else {
					// This component only renders when there is a template, so it is available
					// This window check is not part of the SSR hyrdation
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion, jira/jira-ssr/no-unchecked-globals-usage
					window.open(template!.url, '_blank');
				}
			} else {
				fireClickedEvent('confluencePlaceholderTemplateLink', 'placeholderTemplateContextual');

				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				template && window.open(template.url, '_blank');
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[fireClickedEvent, jswIssueLink],
	);

	if (isEpModalOpen) return <>{epModalEntryPointContainer}</>;

	if (!showDummyLink) return null;

	const renderHeading = () => (
		<DummySectionHeading>
			<DummySectionHeading>
				<SectionHeadingTitle>{formatMessage(messages.newHeading)}</SectionHeadingTitle>
				<ContextualAnalyticsData sourceName="descriptionTemplatesInfoDialog" sourceType={SCREEN}>
					<InfoIconWithPopup />
				</ContextualAnalyticsData>
			</DummySectionHeading>
			<AdControls onClickDismiss={onDismissAd} />
		</DummySectionHeading>
	);

	return (
		<>
			{renderHeading()}
			<ConfluenceLink
				canProvisionConfluence={canProvisionConfluence}
				tenantIsOnFreeEdition={tenantIsOnFreeEdition}
				onClickPlaceholderTemplate={onClickConfluencePlaceholder}
				template={template}
				showDummyLink={showDummyLink}
				projectName={projectName}
				requestStatusLozengeText={requestStatusLozengeText}
			/>
			{fg('placeholder-template-background-provisioning-gate') && (
				<ErrorBoundary
					id="confluence-content-placeholder.background-provisioning-editor"
					packageName="@atlassian/jira-issue-view-base/confluence-dummy-link/background-provisioning-editor"
					render={() => null}
				>
					<BackgroundProvisioningEditor
						issueType={issueType?.name?.toLowerCase?.()}
						cloudId={cloudId}
						projectId={projectId}
					/>
				</ErrorBoundary>
			)}
		</>
	);
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
	onLinkPage: (link: string, analyticsEvent: UIAnalyticsEvent) =>
		dispatch(createConfluencePageLinkRequest(toHref(link), analyticsEvent)),
});

const mapStateToProps = () => ({});

const DummyConfluenceLinkViewWithReduxDispatchProps = connect(
	mapStateToProps,
	mapDispatchToProps,
)(DummyConfluenceLinkViewComponent);

export const DummyConfluenceLinkView = componentWithCondition(
	() => fg('jira_issue_view_expand_modeless_ep'),
	DummyConfluenceLinkViewWithReduxDispatchProps,
	DummyConfluenceLinkViewComponent,
);
